import { useTranslation } from "react-i18next";
import type { MeterSource } from "@/api/districts/types";
import { EmptyCell } from "@/components/ui/empty-cell";
import { fmt } from "./meter-info-utils";

const SOURCE_LABELS: Record<MeterSource["type"], string> = {
	legacy: "Legacy",
	advant: "Advant",
	mqtt: "MQTT",
};

type ColumnKey =
	| "last_communication"
	| "counter"
	| "rssi"
	| "ip"
	| "battery_level";

export function SourcesCard({ sources }: { sources: MeterSource[] }) {
	const { t } = useTranslation();
	if (sources.length === 0) return null;

	const hasValue = (key: ColumnKey) => sources.some((s) => s[key] != null);

	const visibleColumns: ColumnKey[] = (
		["last_communication", "counter", "rssi", "ip", "battery_level"] as const
	).filter(hasValue);

	const columnLabels: Record<ColumnKey, string> = {
		last_communication: t("meterInfo.lastComm"),
		counter: t("meterInfo.counter"),
		rssi: "RSSI",
		ip: "IP",
		battery_level: t("meterInfo.battery"),
	};

	const renderCell = (src: MeterSource, key: ColumnKey) => {
		const value = src[key];
		if (value == null) return <EmptyCell />;
		switch (key) {
			case "last_communication":
				return fmt(value as string);
			case "rssi":
				return `${value} dBm`;
			case "battery_level":
				return `${value}%`;
			default:
				return String(value);
		}
	};

	return (
		<div className="flex flex-col gap-3">
			<h3 className="text-base font-semibold">{t("meterInfo.dataSources")}</h3>
			<div className="rounded-md border">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b bg-muted/50">
							<th className="px-4 py-3 text-left font-medium">
								{t("meterInfo.source")}
							</th>
							<th className="px-4 py-3 text-left font-medium">SN</th>
							{visibleColumns.map((key) => (
								<th key={key} className="px-4 py-3 text-left font-medium">
									{columnLabels[key]}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{sources.map((src) => (
							<tr key={src.sn} className="border-b last:border-0">
								<td className="px-4 py-3 font-medium">
									{SOURCE_LABELS[src.type]}
								</td>
								<td className="px-4 py-3 font-mono text-muted-foreground">
									{src.sn}
								</td>
								{visibleColumns.map((key) => (
									<td
										key={key}
										className="px-4 py-3 font-mono text-muted-foreground"
									>
										{renderCell(src, key)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
